import { useMemo } from 'react';

import { useAuthUser, useForgotPasswordEmail } from '@redux/selectors/auth';

export const enum AuthStatus {
  Unauthenticated = 'unauthenticated',
  Guest = 'guest',
  Authenticated = 'authenticated',
  NotFinishedOnboarding = 'notFinishedOnboarding',
  EmailNotVerified = 'emailNotVerified',
}

export interface UseAuthReturn {
  status: AuthStatus;
  isUnauthenticated: boolean;
  isGuest: boolean;
  isAuthenticated: boolean;
  isAvatarGenerated: boolean;
  isNotFinishedOnboarding: boolean;
  forgotPasswordEmail: string | null;
  canAccessSetPassword: boolean;
  isAuthReady: boolean;
}

export const useAuth = (): UseAuthReturn => {
  const { user: authUser, isAnonymous } = useAuthUser();
  const forgotPasswordEmail = useForgotPasswordEmail();

  const authState = useMemo(() => {
    const isAuthReady = true;
    const isUnauthenticated = !authUser?.id && isAnonymous;
    const isGuest = !!authUser?.id && isAnonymous;
    const hasUserAndNotAnonymous = !!authUser && !isAnonymous;
    const isEmailVerified = !!authUser?.emailVerified;
    const isAvatarGenerated = !!authUser?.image;
    const isOnboarded = !!authUser?.username;
    const isAuthenticated =
      hasUserAndNotAnonymous && isEmailVerified && isOnboarded;
    const isEmailNotVerified = hasUserAndNotAnonymous && !isEmailVerified;
    const isNotFinishedOnboarding =
      hasUserAndNotAnonymous && isEmailVerified && !isOnboarded;

    let status: AuthStatus;
    if (isUnauthenticated) {
      status = AuthStatus.Unauthenticated;
    } else if (isGuest) {
      status = AuthStatus.Guest;
    } else if (isEmailNotVerified) {
      status = AuthStatus.EmailNotVerified;
    } else if (isNotFinishedOnboarding) {
      status = AuthStatus.NotFinishedOnboarding;
    } else if (isAuthenticated) {
      status = AuthStatus.Authenticated;
    } else {
      status = AuthStatus.Unauthenticated;
    }
    const canAccessSetPassword = !!forgotPasswordEmail;

    return {
      status,
      isUnauthenticated,
      isGuest,
      isAuthenticated,
      isAvatarGenerated,
      isNotFinishedOnboarding,
      forgotPasswordEmail,
      canAccessSetPassword,
      isAuthReady,
    };
  }, [authUser, isAnonymous, forgotPasswordEmail]);

  return authState;
};
